/**
 * Parse / serialize helpers for the tenant `theme` value.
 *
 * The `theme` field holds a JSON string — a flat map of branding key → string
 * value, e.g. {"brand":"#e17000","brand2":"#0d3d6e","adminPrimary":"#2970ff"}.
 * The backend keeps it opaque, so all structure lives here. These helpers are
 * deliberately forgiving: a null, empty, non-JSON, or non-object value all
 * parse to {} rather than throwing, because a tenant may carry a legacy
 * plain-string theme from before this editor existed.
 *
 * Lives in @theme/shared because both sides consume it: the admin settings
 * editor WRITES the value, and the client startup (ThemeContext) READS it to
 * apply tenant branding — see useTenantContextStore.
 */

/**
 * @param {string|null|undefined} raw - the stored theme string
 * @returns {Record<string,string>} branding map ({} when absent/unparseable)
 */
export const parseThemeValues = (raw) => {
  if (!raw || typeof raw !== "string") return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    // Keep only string values — the editor deals in strings (hex, preset, font).
    return Object.fromEntries(
      Object.entries(parsed).filter(([, v]) => typeof v === "string"),
    );
  } catch {
    return {};
  }
};

/**
 * Serialize a branding map back to the stored form. Empty entries are dropped;
 * an entirely empty map serializes to "" (not "{}") so the tenant `theme` is
 * cleared rather than storing a meaningless empty object.
 *
 * @param {Record<string,string>} values
 * @returns {string}
 */
export const serializeThemeValues = (values) => {
  const entries = Object.entries(values || {}).filter(
    ([, v]) => typeof v === "string" && v.trim() !== "",
  );
  if (entries.length === 0) return "";
  return JSON.stringify(Object.fromEntries(entries));
};

/**
 * True when `raw` holds a non-empty value that is NOT a branding-map object —
 * i.e. a legacy plain string or some other JSON. The editor uses this to warn
 * that saving will replace the existing value.
 *
 * @param {string|null|undefined} raw
 * @returns {boolean}
 */
export const isLegacyThemeValue = (raw) => {
  if (!raw || typeof raw !== "string" || raw.trim() === "") return false;
  return Object.keys(parseThemeValues(raw)).length === 0;
};
